/**
 * Brand Currency **code** rules — Platform API Reference Manual **§ Currency APIs** (v2.2.9+):
 * Unicode **NFKC**, strip to **`A–Z`, `0–9`, `_`**, uppercase; **4–32** chars; pattern **`[A-Z][A-Z0-9_]{3,31}`**
 * (first character must be a letter so codes do not look like 3-letter fiat tickers).
 * Legacy `MEM_` prefix is stripped after normalization.
 */

export const BRAND_CURRENCY_CODE_MIN_LEN = 4;
export const BRAND_CURRENCY_CODE_MAX_LEN = 32;

/** First character letter; then letters, digits, or underscore (4–32 total). */
const VALID_CODE_RE = /^[A-Z][A-Z0-9_]{3,31}$/;

/**
 * Normalize for API `code`: NFKC, trim, strip legacy `MEM_`, remove disallowed characters, uppercase.
 * Use after {@link sanitizeBrandCurrencyCodeInput} in the UI, or on submit from raw input.
 */
export function normalizeBrandCurrencyCode(raw: string): string {
  let s = raw.normalize('NFKC').trim();
  if (s.toUpperCase().startsWith('MEM_')) {
    s = s.slice(4);
  }
  return s.replace(/[^A-Za-z0-9_]/g, '').toUpperCase();
}

/** For controlled inputs: allow `A–Z`, `a–z`, `0–9`, `_`, uppercase, max length. */
export function sanitizeBrandCurrencyCodeInput(raw: string): string {
  return raw
    .normalize('NFKC')
    .replace(/[^A-Za-z0-9_]/g, '')
    .toUpperCase()
    .slice(0, BRAND_CURRENCY_CODE_MAX_LEN);
}

export function isValidBrandCurrencyCode(code: string): boolean {
  return VALID_CODE_RE.test(code);
}

/**
 * Max user-visible grapheme clusters for API `symbol` (letters, punctuation, emoji, …).
 * Matches issuer-local code max length for consistency.
 */
export const DISPLAY_SYMBOL_MAX_GRAPHEMES = BRAND_CURRENCY_CODE_MAX_LEN;

const DISPLAY_CTRL_RE = /[\u0000-\u001F\u007F]/g;

type IntlSegmenterCtor = new (
  locales?: Intl.LocalesArgument,
  options?: { granularity?: 'grapheme' }
) => { segment: (input: string) => Iterable<{ segment: string }> };

function truncateToMaxGraphemes(s: string, max: number): string {
  const t = s.trim();
  if (!t || max <= 0) return '';
  try {
    const Seg = (Intl as typeof Intl & { Segmenter?: IntlSegmenterCtor }).Segmenter;
    if (typeof Seg === 'function') {
      const seg = new Seg(undefined, { granularity: 'grapheme' });
      let out = '';
      let n = 0;
      for (const { segment } of seg.segment(t)) {
        if (n >= max) break;
        out += segment;
        n += 1;
      }
      return out;
    }
  } catch {
    /* ignore */
  }
  return Array.from(t)
    .slice(0, max)
    .join('');
}

/**
 * Optional API `symbol`: strips ASCII controls, trims, then truncates to {@link DISPLAY_SYMBOL_MAX_GRAPHEMES}
 * grapheme clusters when `Intl.Segmenter` exists (otherwise Unicode code points).
 * Empty string if nothing remains — callers should fall back to the currency **code**.
 */
export function sanitizeDisplaySymbolInput(raw: string): string {
  const cleaned = raw.replace(DISPLAY_CTRL_RE, '');
  return truncateToMaxGraphemes(cleaned, DISPLAY_SYMBOL_MAX_GRAPHEMES);
}

/** API `symbol`: custom display string, or **`code`** when blank / whitespace-only. */
export function resolveBrandCurrencyDisplaySymbol(raw: string | undefined, code: string): string {
  const sym = sanitizeDisplaySymbolInput(raw ?? '');
  return sym || code;
}
